// How contracts read on screen: list dates, ordering, renewal wording, history labels.
// Kept apart from the pages so each rule has its own tests (test/web/display.test.ts).
import { CATEGORIES, FIELD_GROUPS, categoryFor, fieldFor, fieldLabel, groupLabel, isCustomKey, typeFor } from "../../shared/catalog";
import { contractValue, peopleLine, type Contract, type Details, type Party } from "../../shared/contract";

export { formatDetailValue } from "../../shared/contract";
import type { DashboardItem } from "../../shared/dashboard";
import { addMonths, formatDate } from "../../shared/dates";

type ListItem = Pick<DashboardItem<Pick<Contract, "status">>, "contract" | "deadline" | "termEnd">;

/** The date line in the contract list. An ended contract says when it ended, never "Notice by". */
export function listDateLine(item: ListItem, today: string): string {
  if (item.contract.status === "terminated") return item.termEnd ? `Terminated · ended ${formatDate(item.termEnd)}` : "Terminated";
  if (item.termEnd && item.termEnd < today) return `Ended ${formatDate(item.termEnd)}`;
  if (item.deadline) return `Notice by ${formatDate(item.deadline)}`;
  return item.termEnd ? `Ends ${formatDate(item.termEnd)}` : "No end date";
}

/** 0 = live, 1 = ended, 2 = terminated: live contracts come first in the list. */
export function listRank(item: ListItem, today: string): number {
  if (item.contract.status === "terminated") return 2;
  return item.termEnd && item.termEnd < today ? 1 : 0;
}

/** Live first, then by soonest notice deadline (none last), then by name. */
export function sortForList<T extends ListItem & { contract: { title: string } }>(items: T[], today: string): T[] {
  return sortContracts(items, today, "deadline");
}

/** The currency used most in this workspace (ties: the one seen first), or "" when none is set. */
export function usualCurrency(contracts: readonly Pick<Contract, "currency">[] | undefined): string {
  const counts = new Map<string, number>();
  for (const c of contracts ?? []) if (c.currency) counts.set(c.currency, (counts.get(c.currency) ?? 0) + 1);
  let best = "";
  for (const [code, n] of counts) if (n > (counts.get(best) ?? 0)) best = code;
  return best;
}

/** Active contracts with no end date: they never get a reminder. */
export function undatedActive<T extends Pick<Contract, "status" | "endDate" | "deletedAt">>(contracts: readonly T[]): T[] {
  return contracts.filter((c) => !c.deletedAt && c.status === "active" && !c.endDate);
}

/** The renewal row on the contract page, in the same words as the form. */
export function renewalText(c: Pick<Contract, "renewalType" | "renewalTermMonths">): string {
  if (c.renewalType === "none") return "Ends on the end date";
  if (c.renewalType === "auto") return c.renewalTermMonths ? `Renews automatically every ${c.renewalTermMonths} months` : "Renews automatically";
  return `Renewable if you choose${c.renewalTermMonths ? `, for ${c.renewalTermMonths} months` : ""}`;
}

/** The new end date offered when marking a contract renewed: a year after the current end. */
export function suggestedRenewalEnd(termEnd: string | null): string | null {
  return termEnd ? addMonths(termEnd, 12) : null;
}

export const HISTORY_LABELS: Record<string, string> = {
  "contract.created": "Added",
  "contract.updated": "Edited",
  "contract.renewed": "Marked renewed",
  "contract.renegotiated": "Marked renegotiated",
  "contract.terminated": "Marked terminated",
  "contract.deadline_rolled": "Moved to the next term",
  "reminder.sent": "Reminder sent",
  "document.uploaded": "File added",
  "document.deleted": "File deleted",
};

/** A readable label for an audit event, e.g. "contract.renewed" → "Marked renewed"; unknown types are tidied, not hidden. */
export function historyLabel(type: string): string {
  const known = HISTORY_LABELS[type];
  if (known) return known;
  const words = type.replace(/^\w+\./, "").replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** "Nov 1, 2026, 10:31 PM": the same month-name style as the dates elsewhere. */
export function formatDateTime(iso: string, locale?: string, timeZone?: string): string {
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", timeZone }).format(new Date(iso));
}

// --- types, people and terms ---------------------------------------------------------

/** "Rent agreement · Property & real estate", or "" when the contract has no type. */
export function typeLine(type: string | null | undefined): string {
  const t = typeFor(type);
  return t ? `${t.label} · ${categoryFor(t.category)?.label ?? ""}` : "";
}

/** Terms grouped by topic in catalogue order (custom terms last), each in the order it was added. */
export function groupedDetails(details: Details): { group: string; label: string; items: { key: string; label: string; value: string }[] }[] {
  const order = new Map(FIELD_GROUPS.map((g, i) => [g.id, i]));
  const groups = new Map<string, { key: string; label: string; value: string }[]>();
  for (const [key, value] of Object.entries(details ?? {})) {
    const group = isCustomKey(key) ? "custom" : (fieldFor(key)?.group ?? "custom");
    groups.set(group, [...(groups.get(group) ?? []), { key, label: fieldLabel(key), value }]);
  }
  return [...groups]
    .sort(([a], [b]) => (order.get(a) ?? 999) - (order.get(b) ?? 999))
    .map(([group, items]) => ({ group, label: group === "custom" ? "Other terms" : groupLabel(group), items }));
}

/** The people line in the list: "Lessee: Asha Rao · Guarantor: R. Shah". */
export function partiesLine(parties: readonly Party[] | undefined): string {
  return peopleLine({ parties: [...(parties ?? [])], counterparty: null });
}

export type SortKey = "deadline" | "name" | "added" | "value" | "category";

export const SORTS: Record<SortKey, string> = {
  deadline: "Soonest deadline",
  name: "Name (A–Z)",
  added: "Recently added",
  value: "Value (highest first)",
  category: "Category",
};

type Sortable = ListItem & {
  contract: { title: string; createdAt?: string; valueMinor?: number | null; currency?: string | null; details?: Details; type?: string | null };
};
/** The total value for sorting, from the "Total contract value" term (or the old value column). */
const valueOf = (c: Sortable["contract"]) => contractValue({ details: c.details ?? {}, valueMinor: c.valueMinor ?? null, currency: c.currency ?? null })?.minor ?? -1;

/**
 * Orders the list. Live contracts always come before ended and terminated ones; within that, by
 * the chosen key. "Value" compares amounts as stored, so mixed currencies aren't converted.
 */
export function sortContracts<T extends Sortable>(items: T[], today: string, by: SortKey = "deadline"): T[] {
  const catIndex = new Map(CATEGORIES.map((c, i) => [c.id, i]));
  const category = (i: T) => {
    const t = typeFor(i.contract.type);
    return t ? (catIndex.get(t.category) ?? 998) : 999;
  };
  const byDeadline = (a: T, b: T) => (a.deadline ?? "9999").localeCompare(b.deadline ?? "9999");
  const byName = (a: T, b: T) => a.contract.title.localeCompare(b.contract.title);
  const cmp: Record<SortKey, (a: T, b: T) => number> = {
    deadline: (a, b) => byDeadline(a, b) || byName(a, b),
    name: byName,
    added: (a, b) => (b.contract.createdAt ?? "").localeCompare(a.contract.createdAt ?? "") || byName(a, b),
    value: (a, b) => valueOf(b.contract) - valueOf(a.contract) || byName(a, b),
    category: (a, b) =>
      category(a) - category(b) ||
      (typeFor(a.contract.type)?.label ?? "").localeCompare(typeFor(b.contract.type)?.label ?? "") ||
      byDeadline(a, b) ||
      byName(a, b),
  };
  return [...items].sort((a, b) => listRank(a, today) - listRank(b, today) || cmp[by](a, b));
}
